/* eslint-disable react-hooks/refs -- Intentional ref-sync pattern: keep refs
   current so async callbacks (stream, load) never read stale closures. */
import { useRef, useCallback } from 'react';
import { useFiles, type FilesMap } from '@/client/lib/filesContext';
import { normalizeFiles } from '@/client/lib/normalizeFiles';
import { usePlan, extractPlan } from '@/client/lib/planContext';
import { useTasks, extractTasks } from '@/client/lib/tasksContext';
import { useStreamingStatusRef, useActiveStreamRef, useJoinedStreamRef } from '@/client/lib/streamingStatusContext';


/**
 * Encapsulates the ref-wiring pattern shared by both RuntimeProvider and
 * RuntimeProvider. Returns stable refs for async callbacks so they
 * never read stale closures.
 */
export function useRuntimeRefs(assistantId: string) {
  const assistantIdRef = useRef(assistantId);
  assistantIdRef.current = assistantId;

  const { setFiles, takeDirtyFiles } = useFiles();
  const setFilesRef = useRef(setFiles);
  setFilesRef.current = setFiles;
  const takeDirtyFilesRef = useRef(takeDirtyFiles);
  takeDirtyFilesRef.current = takeDirtyFiles;

  const { setPlan } = usePlan();
  const setPlanRef = useRef(setPlan);
  setPlanRef.current = setPlan;

  const { setTasks } = useTasks();
  const setTasksRef = useRef(setTasks);
  setTasksRef.current = setTasks;

  const setStreamingStatus = useStreamingStatusRef();
  const activeStreams = useActiveStreamRef();
  const joinedStream = useJoinedStreamRef();
  const conversationTitleRef = useRef<string | null>(null);

  const extractFromValues = useCallback((values: unknown, threadId?: string) => {
    const v = values as Record<string, unknown> | undefined;
    setFilesRef.current(normalizeFiles(v?.files));
    setPlanRef.current(extractPlan(v), threadId);
    setTasksRef.current(extractTasks(v));

    // Track conversation title from middleware state
    const cm = v?.conversation_metadata as { title?: string } | undefined;
    if (cm?.title) conversationTitleRef.current = cm.title;
  }, []);

  const takeDirtyFilesSnapshot = useCallback(() => takeDirtyFilesRef.current() ?? undefined, []);

  const clearStateRefs = useCallback(() => {
    setFilesRef.current({} as FilesMap);
    setPlanRef.current(null, null);
    setTasksRef.current([]);
  }, []);

  const getConversationTitle = useCallback(() => conversationTitleRef.current, []);
  const clearConversationTitle = useCallback(() => { conversationTitleRef.current = null; }, []);

  return {
    assistantIdRef,
    setStreamingStatus,
    activeStreams,
    joinedStream,
    extractFromValues,
    takeDirtyFiles: takeDirtyFilesSnapshot,
    clearStateRefs,
    getConversationTitle,
    clearConversationTitle,
  };
}
